import { FilmIcon } from 'lucide-react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { Button } from '~/components/ui/button';
import { Card } from '~/components/ui/card';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { getCellRects } from '~/features/grid/cellRects';
import { MIN_GRID_GAP } from '~/features/grid/constants';
import { useStore } from '~/store';
import type { BlobCandidate, Rect } from '~/types/pipeline';
import type { BoxRef } from '~/types/selection';
import { boxRefEquals } from '~/types/selection';
import { imageOrigin } from './imageOrigin';

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

/** Union bounding rect of any list of source-image rects, or null if empty. */
export function unionBounds(rects: Rect[]): Rect | null {
  if (rects.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const r of rects) {
    if (r.x < minX) minX = r.x;
    if (r.y < minY) minY = r.y;
    if (r.x + r.w > maxX) maxX = r.x + r.w;
    if (r.y + r.h > maxY) maxY = r.y + r.h;
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

/** Pick the rects of the selected cells, in selection order, skipping unknowns. */
function selectedRects(cells: Array<{ box: BoxRef; rect: Rect }>, selected: BoxRef[]): Rect[] {
  const out: Rect[] = [];
  for (const s of selected) {
    const cell = cells.find((c) => boxRefEquals(c.box, s));
    if (cell) out.push(cell.rect);
  }
  return out;
}

/** Union bounding rect (source-image px) of the selected cells, or null. */
export function selectionBounds(
  cells: Array<{ box: BoxRef; rect: Rect }>,
  selected: BoxRef[],
): Rect | null {
  return unionBounds(selectedRects(cells, selected));
}

/** Return the single shared value across rects on getDim, or null if mixed/empty. */
export function sharedRectDimension(rects: Rect[], getDim: (r: Rect) => number): number | null {
  if (rects.length === 0) return null;
  const firstRect = rects[0];
  if (!firstRect) return null;
  const first = getDim(firstRect);
  for (let i = 1; i < rects.length; i++) {
    const rect = rects[i];
    if (!rect) return null;
    if (getDim(rect) !== first) return null;
  }
  return first;
}

/** Return the single shared value if all boxes agree on getDim, else null. */
export function sharedDimension(
  boxes: BoxRef[],
  cells: Array<{ box: BoxRef; rect: Rect }>,
  getDim: (r: Rect) => number,
): number | null {
  return sharedRectDimension(selectedRects(cells, boxes), getDim);
}

/** Resize a rect around its center; clamp by min size. */
export function centroidResize(
  rect: Rect,
  next: { w?: number; h?: number },
  minSize = MIN_GRID_GAP,
): Rect {
  const cx = rect.x + rect.w / 2;
  const cy = rect.y + rect.h / 2;
  const w = next.w != null ? Math.max(minSize, Math.round(next.w)) : rect.w;
  const h = next.h != null ? Math.max(minSize, Math.round(next.h)) : rect.h;
  return {
    x: Math.round(cx - w / 2),
    y: Math.round(cy - h / 2),
    w,
    h,
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const POPOVER_GAP = 8;

function useContainerRect(ref: React.RefObject<HTMLDivElement | null>) {
  const [rect, setRect] = useState<DOMRect | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setRect(el.getBoundingClientRect());
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return rect;
}

function formatDim(v: number | null): string {
  return v != null ? String(Math.round(v)) : '';
}

function DimInput({
  id,
  label,
  sharedValue,
  onCommit,
}: {
  id: string;
  label: string;
  sharedValue: number | null;
  onCommit: (v: number) => void;
}) {
  const [local, setLocal] = useState(formatDim(sharedValue));

  // Sync when external selection changes
  useEffect(() => {
    setLocal(formatDim(sharedValue));
  }, [sharedValue]);

  const commit = () => {
    const n = parseInt(local, 10);
    if (!Number.isFinite(n)) {
      setLocal(formatDim(sharedValue));
      return;
    }
    const clamped = Math.max(MIN_GRID_GAP, n);
    onCommit(clamped);
    setLocal(String(clamped));
  };

  return (
    <div className="flex items-center gap-2">
      <Label htmlFor={id} className="w-4 shrink-0 text-xs text-muted-foreground">
        {label}
      </Label>
      <Input
        id={id}
        type="number"
        className="h-7 w-20 text-xs"
        value={local}
        placeholder="—"
        min={MIN_GRID_GAP}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            commit();
            (e.target as HTMLInputElement).blur();
          } else if (e.key === 'Escape') {
            setLocal(formatDim(sharedValue));
            (e.target as HTMLInputElement).blur();
          }
        }}
      />
    </div>
  );
}

type SelectionView = {
  /** Union bounds of the selection in source-image px, or null when empty. */
  bounds: Rect | null;
  /** Shared width across the selection, or null if mixed. */
  sharedW: number | null;
  /** Shared height across the selection, or null if mixed. */
  sharedH: number | null;
  /** Number of selected items. */
  count: number;
  /** Title shown in the popover. */
  title: string;
  /** Apply a width to every selected item (centroid-anchored). */
  commitWidth: (w: number) => void;
  /** Apply a height to every selected item (centroid-anchored). */
  commitHeight: (h: number) => void;
  /** Create a clip from the current selection and clear it. */
  createClip: () => void;
};

function useGridSelectionView(): SelectionView {
  const selectedBoxes = useStore(useShallow((s) => s.editor.selectedBoxes));
  const grid = useStore(
    useShallow((s) => ({
      cropRect: s.grid.cropRect,
      rowDividers: s.grid.rowDividers,
      colDividers: s.grid.colDividers,
    })),
  );
  const setBoxesWidth = useStore((s) => s.setBoxesWidth);
  const setBoxesHeight = useStore((s) => s.setBoxesHeight);
  const addClip = useStore((s) => s.addClip);
  const clearBoxSelection = useStore((s) => s.clearBoxSelection);

  const cells = useMemo(() => getCellRects(grid), [grid]);
  const rects = useMemo(() => selectedRects(cells, selectedBoxes), [cells, selectedBoxes]);
  const bounds = useMemo(() => unionBounds(rects), [rects]);

  const sharedW = sharedRectDimension(rects, (r) => r.w);
  const sharedH = sharedRectDimension(rects, (r) => r.h);
  const first = selectedBoxes[0];
  const title =
    selectedBoxes.length === 1 && first
      ? `Cell R${first.row + 1}C${first.col + 1}`
      : `${selectedBoxes.length} boxes selected`;

  return {
    bounds,
    sharedW,
    sharedH,
    count: selectedBoxes.length,
    title,
    commitWidth: (v) => {
      if (selectedBoxes.length > 0) setBoxesWidth(selectedBoxes, v);
    },
    commitHeight: (v) => {
      if (selectedBoxes.length > 0) setBoxesHeight(selectedBoxes, v);
    },
    createClip: () => {
      if (selectedBoxes.length === 0) return;
      addClip({ kind: 'cells', cells: selectedBoxes });
      clearBoxSelection();
    },
  };
}

function effectiveBlobBBox(
  blobs: BlobCandidate[],
  overrides: Record<number, { bbox?: Rect }>,
  index: number,
): Rect | null {
  const blob = blobs[index];
  if (!blob) return null;
  return overrides[index]?.bbox ?? blob.bbox;
}

function usePinSelectionView(): SelectionView {
  const selectedBlobs = useStore(useShallow((s) => s.editor.selectedBlobs));
  const blobs = useStore((s) => s.pin.blobs);
  const overrides = useStore((s) => s.pin.overrides);
  const setBlobOverrides = useStore((s) => s.setBlobOverrides);
  const addClip = useStore((s) => s.addClip);
  const clearBlobSelection = useStore((s) => s.clearBlobSelection);

  const rects = useMemo(() => {
    if (!blobs) return [];
    const out: Rect[] = [];
    for (const i of selectedBlobs) {
      const r = effectiveBlobBBox(blobs, overrides, i);
      if (r) out.push(r);
    }
    return out;
  }, [blobs, overrides, selectedBlobs]);

  const bounds = useMemo(() => unionBounds(rects), [rects]);
  const sharedW = sharedRectDimension(rects, (r) => r.w);
  const sharedH = sharedRectDimension(rects, (r) => r.h);

  const firstSelectedBlob = selectedBlobs[0];
  const title =
    selectedBlobs.length === 1 && firstSelectedBlob !== undefined
      ? `Frame ${firstSelectedBlob + 1}`
      : `${selectedBlobs.length} frames selected`;

  const applyDim = (dim: 'w' | 'h', v: number) => {
    if (!blobs || selectedBlobs.length === 0) return;
    const updates: Record<number, { bbox: Rect }> = {};
    for (const i of selectedBlobs) {
      const cur = effectiveBlobBBox(blobs, overrides, i);
      if (!cur) continue;
      const next = centroidResize(cur, dim === 'w' ? { w: v } : { h: v });
      updates[i] = { bbox: next };
    }
    setBlobOverrides(updates);
  };

  return {
    bounds,
    sharedW,
    sharedH,
    count: selectedBlobs.length,
    title,
    commitWidth: (v) => applyDim('w', v),
    commitHeight: (v) => applyDim('h', v),
    createClip: () => {
      if (selectedBlobs.length === 0) return;
      addClip({ kind: 'blobs', blobs: selectedBlobs });
      clearBlobSelection();
    },
  };
}

export function SelectionPopover() {
  const containerRef = useRef<HTMLDivElement>(null);
  const containerRect = useContainerRect(containerRef);

  const framingMode = useStore((s) => s.pin.framingMode);
  const viewport = useStore((s) => s.editor.viewport);
  const stageScale = useStore((s) => s.editor.stageScale);
  const dims = useStore((s) => s.source.dimensions);

  // Both hooks always run (rules of hooks); the unused side just returns count: 0.
  const gridView = useGridSelectionView();
  const pinView = usePinSelectionView();
  const view = framingMode === 'pin' ? pinView : gridView;
  const { bounds, sharedW, sharedH, count, title, commitWidth, commitHeight, createClip } = view;

  // Derive popover position in CSS px relative to the container. Computed
  // (not stateful) so a fresh `bounds` object identity each render can't drive
  // a setState→render loop.
  const popoverStyle = useMemo<React.CSSProperties>(() => {
    if (!bounds || !dims || !containerRect) return { display: 'none' };

    const containerW = containerRect.width;
    const containerH = containerRect.height;

    const origin = imageOrigin(
      containerW,
      containerH,
      dims.w,
      dims.h,
      stageScale,
      viewport.panX,
      viewport.panY,
    );

    const left = origin.x + bounds.x * stageScale;
    const top = origin.y + bounds.y * stageScale;
    const width = bounds.w * stageScale;
    const height = bounds.h * stageScale;

    const popoverH = 96;
    const popoverW = 160;

    let posTop = top - popoverH - POPOVER_GAP;
    if (posTop < 0) {
      posTop = top + height + POPOVER_GAP;
      if (posTop + popoverH > containerH) {
        posTop = Math.max(0, top - popoverH - POPOVER_GAP);
      }
    }

    let posLeft = left + width / 2 - popoverW / 2;
    posLeft = Math.max(0, Math.min(containerW - popoverW, posLeft));

    return { position: 'absolute', top: posTop, left: posLeft };
  }, [bounds, dims, containerRect, stageScale, viewport]);

  // Always mount the wrapper so `useContainerRect` captures the element on
  // first render — otherwise the rect stays null until a resize event.
  return (
    <div ref={containerRef} className="pointer-events-none absolute inset-0">
      {count > 0 && (
        <Card
          style={popoverStyle}
          className="pointer-events-auto z-20 flex flex-col gap-2 p-3 shadow-lg"
          aria-label={title}
        >
          <span className="text-xs font-medium leading-none">{title}</span>
          <div className="flex flex-col gap-1.5">
            <DimInput id="sel-pop-width" label="W" sharedValue={sharedW} onCommit={commitWidth} />
            <DimInput id="sel-pop-height" label="H" sharedValue={sharedH} onCommit={commitHeight} />
          </div>
          <Button size="xs" variant="outline" onClick={createClip} className="gap-1">
            <FilmIcon />
            Create clip
          </Button>
        </Card>
      )}
    </div>
  );
}
